import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import './css/OpportunitySidebar.css';
import './css/BaobabDesignSystem.css';

const OpportunitySidebar = ({ opportunity, isOpen, onClose, onUpdate }) => {
  const [activeTab, setActiveTab] = useState('summary');
  const [comments, setComments] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [showMentionSuggestions, setShowMentionSuggestions] = useState(false);
  const [mentionFilter, setMentionFilter] = useState('');
  const [cursorPosition, setCursorPosition] = useState(0);
  const [editingOpportunity, setEditingOpportunity] = useState({ ...opportunity });
  const [isLoading, setIsLoading] = useState(false);
  const [showMentions, setShowMentions] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [availableCommercials, setAvailableCommercials] = useState([]);
  const [availableCompanies, setAvailableCompanies] = useState([]);
  
  const { user } = useAuth();
  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  useEffect(() => {
    if (isOpen && opportunity) {
      setEditingOpportunity({ ...opportunity });
      fetchComments();
      fetchAttachments();
      fetchUsers();
      fetchCompanies();
    }
  }, [isOpen, opportunity]);

  // Gestion de la touche Échap pour fermer le sidebar
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      // Empêcher le scroll du body quand le sidebar est ouvert
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const fetchComments = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/opportunities/${opportunity.id}/comments`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setComments(data);
      }
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  const fetchAttachments = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/opportunities/${opportunity.id}/attachments`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAttachments(data);
      }
    } catch (error) {
      console.error('Error fetching attachments:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/users`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAvailableUsers(data);
        
        // Filtrer les commerciaux pour la réassignation
        const commercials = data.filter(u => 
          ['admin', 'manager', 'commercial'].includes(u.role)
        );
        setAvailableCommercials(commercials);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchCompanies = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/crm/clients`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAvailableCompanies(data);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const handleSaveChanges = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/opportunities/${opportunity.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editingOpportunity)
      });
      
      if (response.ok) {
        const updatedOpportunity = await response.json();
        onUpdate(updatedOpportunity);
        setEditingOpportunity(updatedOpportunity);
      } else {
        alert('Erreur lors de la sauvegarde');
      }
    } catch (error) {
      console.error('Error saving changes:', error);
      alert('Erreur lors de la sauvegarde');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCommentChange = (e) => {
    const value = e.target.value;
    const cursorPos = e.target.selectionStart;
    setNewComment(value);
    setCursorPosition(cursorPos);
    
    // Détecter les mentions @
    const textBeforeCursor = value.substring(0, cursorPos);
    const mentionMatch = textBeforeCursor.match(/@(\w*)$/);
    
    if (mentionMatch) {
      const filter = mentionMatch[1].toLowerCase();
      setMentionFilter(filter);
      setShowMentionSuggestions(true);
    } else {
      setShowMentionSuggestions(false);
      setMentionFilter('');
    }
  };

  const insertMention = (user) => {
    const textBeforeCursor = newComment.substring(0, cursorPosition);
    const textAfterCursor = newComment.substring(cursorPosition);
    
    // Trouver le début de la mention
    const mentionStartIndex = textBeforeCursor.lastIndexOf('@');
    const beforeMention = textBeforeCursor.substring(0, mentionStartIndex);
    const mention = `@${user.username}`;
    const newText = beforeMention + mention + ' ' + textAfterCursor;
    
    setNewComment(newText);
    setShowMentionSuggestions(false);
    setMentionFilter('');
  };

  const getFilteredUsers = () => {
    if (!mentionFilter) return availableUsers.slice(0, 5);
    
    return availableUsers.filter(user => 
      user.username.toLowerCase().includes(mentionFilter) ||
      user.full_name.toLowerCase().includes(mentionFilter)
    ).slice(0, 5);
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    
    // Extraire les mentions (@username)
    const mentions = [];
    const mentionRegex = /@(\w+)/g;
    let match;
    while ((match = mentionRegex.exec(newComment)) !== null) {
      const mentionedUser = availableUsers.find(u => 
        u.username.toLowerCase() === match[1].toLowerCase() ||
        u.full_name.toLowerCase().includes(match[1].toLowerCase())
      );
      if (mentionedUser) {
        mentions.push(mentionedUser.id);
      }
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/opportunities/${opportunity.id}/comments`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          opportunity_id: opportunity.id,
          comment_text: newComment,
          mentions: mentions
        })
      });
      
      if (response.ok) {
        setNewComment('');
        fetchComments();
      } else {
        alert('Erreur lors de l\'ajout du commentaire');
      }
    } catch (error) {
      console.error('Error adding comment:', error);
      alert('Erreur lors de l\'ajout du commentaire');
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/opportunities/${opportunity.id}/attachments`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData
      });
      
      if (response.ok) {
        fetchAttachments();
      } else {
        alert('Erreur lors du téléchargement du fichier');
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Erreur lors du téléchargement du fichier');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      'prospection': '#6B7280',
      'qualification': '#3B82F6',
      'proposition': '#F59E0B',
      'négociation': '#EF4444',
      'gagnée': '#22C55E',
      'perdue': '#6B7280'
    };
    return colors[status] || '#6B7280';
  };

  if (!isOpen) return null;

  return (
    <div 
      className="opportunity-sidebar-overlay"
      onClick={onClose} // Fermer en cliquant sur l'overlay
    >
      <div 
        className="opportunity-sidebar"
        onClick={(e) => e.stopPropagation()} // Empêcher la fermeture quand on clique sur le sidebar lui-même
      >
        <div className="sidebar-header">
          <div className="sidebar-title">
            <h2 className="text-xl font-semibold text-gray-900 truncate">
              {opportunity.nom_opportunite}
            </h2>
            <span 
              className="status-badge"
              style={{ backgroundColor: getStatusColor(opportunity.statut), color: 'white' }}
            >
              {opportunity.statut}
            </span>
          </div>
          <button
            onClick={onClose}
            className="sidebar-close-btn"
            title="Fermer (Échap)" // Indication tooltip
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs Navigation - Force visibility */}
        <div className="sidebar-tabs" style={{ display: 'flex', minHeight: '60px' }}>
          <button
            className={`tab-button ${activeTab === 'summary' ? 'active' : ''}`}
            onClick={() => setActiveTab('summary')}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            📊 Résumé
          </button>
          <button
            className={`tab-button ${activeTab === 'timeline' ? 'active' : ''}`}
            onClick={() => setActiveTab('timeline')}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            📋 Timeline
          </button>
          <button
            className={`tab-button ${activeTab === 'comments' ? 'active' : ''}`}
            onClick={() => setActiveTab('comments')}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            💬 Commentaires ({comments.length})
          </button>
          <button
            className={`tab-button ${activeTab === 'documents' ? 'active' : ''}`}
            onClick={() => setActiveTab('documents')}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            📎 Documents ({attachments.length})
          </button>
        </div>

        <div className="sidebar-content">
          {/* Summary Tab */}
          {activeTab === 'summary' && (
            <div className="tab-content">
              <div className="summary-section">
                <h3 className="section-title">Informations générales</h3>
                
                <div className="form-group">
                  <label className="form-label">Client / Société</label>
                  <select
                    value={editingOpportunity.client_name || ''}
                    onChange={(e) => {
                      const selectedCompany = availableCompanies.find(c => c.name === e.target.value);
                      setEditingOpportunity({
                        ...editingOpportunity,
                        client_name: e.target.value,
                        client_id: selectedCompany ? selectedCompany.id : null
                      });
                    }}
                    className="form-input"
                  >
                    <option value="">Sélectionner une société</option>
                    {availableCompanies.map(company => (
                      <option key={company.id} value={company.name}>
                        {company.name} - {company.city || 'N/A'}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Commercial responsable</label>
                  <select
                    value={editingOpportunity.commercial_responsable || ''}
                    onChange={(e) => {
                      const selectedCommercial = availableCommercials.find(c => 
                        `${c.full_name} (${c.email})` === e.target.value
                      );
                      setEditingOpportunity({
                        ...editingOpportunity,
                        commercial_responsable: e.target.value
                      });
                    }}
                    className="form-input"
                  >
                    <option value="">Sélectionner un commercial</option>
                    {availableCommercials.map(commercial => (
                      <option 
                        key={commercial.id} 
                        value={`${commercial.full_name} (${commercial.email})`}
                      >
                        {commercial.full_name} - {commercial.role}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Probabilité (%)</label>
                  <div className="flex items-center gap-3">
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={editingOpportunity.probabilite || 0}
                      onChange={(e) => setEditingOpportunity({
                        ...editingOpportunity,
                        probabilite: parseInt(e.target.value)
                      })}
                      className="flex-1"
                    />
                    <span className="font-semibold text-gray-900 w-12">
                      {editingOpportunity.probabilite}%
                    </span>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Statut de l'opportunité</label>
                  <select
                    value={editingOpportunity.statut || 'prospection'}
                    onChange={(e) => setEditingOpportunity({
                      ...editingOpportunity,
                      statut: e.target.value
                    })}
                    className="form-input"
                  >
                    <option value="prospection">🔍 Prospection</option>
                    <option value="qualification">✅ Qualification</option>
                    <option value="proposition">📋 Proposition</option>
                    <option value="négociation">🤝 Négociation</option>
                    <option value="gagnée">🎉 Gagnée</option>
                    <option value="perdue">❌ Perdue</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Valeur estimée</label>
                  <input
                    type="number"
                    value={editingOpportunity.valeur_estimee || 0}
                    onChange={(e) => setEditingOpportunity({
                      ...editingOpportunity,
                      valeur_estimee: parseFloat(e.target.value) || 0
                    })}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Date de clôture prévue</label>
                  <input
                    type="date"
                    value={editingOpportunity.date_cloture_prevue?.split('T')[0] || ''}
                    onChange={(e) => setEditingOpportunity({
                      ...editingOpportunity,
                      date_cloture_prevue: e.target.value ? new Date(e.target.value).toISOString() : null,
                      expected_close_date: e.target.value ? new Date(e.target.value).toISOString() : null
                    })}
                    className="form-input"
                  />
                </div>

                {editingOpportunity.statut === 'perdue' && (
                  <div className="form-group">
                    <label className="form-label">Raison de perte</label>
                    <textarea
                      value={editingOpportunity.lost_reason || ''}
                      onChange={(e) => setEditingOpportunity({
                        ...editingOpportunity,
                        lost_reason: e.target.value
                      })}
                      className="form-input"
                      rows="3"
                      placeholder="Expliquez pourquoi cette opportunité a été perdue..."
                    />
                  </div>
                )}

                <button
                  onClick={handleSaveChanges}
                  disabled={isLoading}
                  className="save-btn"
                >
                  {isLoading ? 'Sauvegarde...' : 'Sauvegarder les modifications'}
                </button>
              </div>

              <div className="summary-section">
                <h3 className="section-title">Détails</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">N° Opportunité:</span>
                    <span className="detail-value">{opportunity.numero_opportunite}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Client:</span>
                    <span className="detail-value">{opportunity.client_name}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Commercial:</span>
                    <span className="detail-value">{opportunity.commercial_responsable}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Créée le:</span>
                    <span className="detail-value">
                      {formatDate(opportunity.date_creation)}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Dernière MAJ:</span>
                    <span className="detail-value">
                      {formatDate(opportunity.date_derniere_mise_a_jour || opportunity.updated_at)}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Valeur:</span>
                    <span className="detail-value font-bold text-green-600">
                      {formatCurrency(opportunity.valeur_estimee)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Timeline Tab */}
          {activeTab === 'timeline' && (
            <div className="tab-content">
              <div className="timeline-container">
                <h3 className="section-title">Historique des actions</h3>
                
                <div className="timeline">
                  <div className="timeline-item">
                    <div className="timeline-marker create"></div>
                    <div className="timeline-content">
                      <div className="timeline-header">
                        <span className="timeline-action">Opportunité créée</span>
                        <span className="timeline-date">{formatDate(opportunity.date_creation)}</span>
                      </div>
                      <p className="timeline-description">
                        L'opportunité a été créée par {opportunity.created_by}
                      </p>
                    </div>
                  </div>

                  {opportunity.date_derniere_mise_a_jour !== opportunity.date_creation && (
                    <div className="timeline-item">
                      <div className="timeline-marker update"></div>
                      <div className="timeline-content">
                        <div className="timeline-header">
                          <span className="timeline-action">Dernière modification</span>
                          <span className="timeline-date">
                            {formatDate(opportunity.date_derniere_mise_a_jour || opportunity.updated_at)}
                          </span>
                        </div>
                        <p className="timeline-description">
                          L'opportunité a été mise à jour
                        </p>
                      </div>
                    </div>
                  )}

                  {opportunity.statut === 'gagnée' && opportunity.date_cloture_reelle && (
                    <div className="timeline-item">
                      <div className="timeline-marker success"></div>
                      <div className="timeline-content">
                        <div className="timeline-header">
                          <span className="timeline-action">Opportunité gagnée</span>
                          <span className="timeline-date">{formatDate(opportunity.date_cloture_reelle)}</span>
                        </div>
                        <p className="timeline-description">
                          L'opportunité a été marquée comme gagnée
                        </p>
                      </div>
                    </div>
                  )}

                  {opportunity.statut === 'perdue' && opportunity.date_cloture_reelle && (
                    <div className="timeline-item">
                      <div className="timeline-marker failed"></div>
                      <div className="timeline-content">
                        <div className="timeline-header">
                          <span className="timeline-action">Opportunité perdue</span>
                          <span className="timeline-date">{formatDate(opportunity.date_cloture_reelle)}</span>
                        </div>
                        <p className="timeline-description">
                          Raison: {opportunity.lost_reason || 'Non spécifiée'}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Comments Tab */}
          {activeTab === 'comments' && (
            <div className="tab-content">
              <div className="comments-container">
                <h3 className="section-title">Fil de commentaires</h3>
                
                {/* New Comment Form */}
                <div className="new-comment-form">
                  <div className="form-group">
                    <div className="comment-input-container">
                      <textarea
                        value={newComment}
                        onChange={handleCommentChange}
                        placeholder="Ajouter un commentaire... (utilisez @username pour mentionner quelqu'un)"
                        className="form-input"
                        rows="3"
                      />
                      
                      {/* Mentions Suggestions */}
                      {showMentionSuggestions && (
                        <div className="mention-suggestions">
                          {getFilteredUsers().map(user => (
                            <div
                              key={user.id}
                              className="mention-suggestion"
                              onClick={() => insertMention(user)}
                            >
                              <span className="mention-avatar">👤</span>
                              <div className="mention-info">
                                <div className="mention-name">{user.full_name}</div>
                                <div className="mention-username">@{user.username}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={handleAddComment}
                    disabled={!newComment.trim()}
                    className="add-comment-btn"
                  >
                    💬 Ajouter un commentaire
                  </button>
                </div>

                {/* Comments List */}
                <div className="comments-list">
                  {comments.length === 0 ? (
                    <div className="empty-state">
                      <p className="text-gray-500 text-center py-8">
                        Aucun commentaire pour le moment
                      </p>
                    </div>
                  ) : (
                    comments.map((comment) => (
                      <div key={comment.id} className="comment-item">
                        <div className="comment-header">
                          <span className="comment-author">{comment.user_name}</span>
                          <span className="comment-date">{formatDate(comment.created_at)}</span>
                          {comment.is_edited && (
                            <span className="edited-badge">modifié</span>
                          )}
                        </div>
                        <div className="comment-content">
                          {comment.comment_text}
                        </div>
                        {comment.mentions && comment.mentions.length > 0 && (
                          <div className="comment-mentions">
                            <span className="mentions-label">Mentions:</span>
                            {comment.mentions.map((mentionId, index) => {
                              const mentionedUser = availableUsers.find(u => u.id === mentionId);
                              return mentionedUser ? (
                                <span key={index} className="mention-badge">
                                  @{mentionedUser.username}
                                </span>
                              ) : null;
                            })}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Documents Tab */}
          {activeTab === 'documents' && (
            <div className="tab-content">
              <div className="documents-container">
                <h3 className="section-title">Pièces jointes</h3>
                
                {/* Upload Form */}
                <div className="upload-form">
                  <input
                    type="file"
                    onChange={handleFileUpload}
                    className="file-input"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                  />
                  <p className="upload-help">
                    Types de fichiers acceptés: PDF, Word, Excel, Images (max 5MB)
                  </p>
                </div>

                {/* Attachments List */}
                <div className="attachments-list">
                  {attachments.length === 0 ? (
                    <div className="empty-state">
                      <p className="text-gray-500 text-center py-8">
                        Aucune pièce jointe
                      </p>
                    </div>
                  ) : (
                    attachments.map((attachment) => (
                      <div key={attachment.id} className="attachment-item">
                        <div className="attachment-icon">
                          📎
                        </div>
                        <div className="attachment-info">
                          <div className="attachment-name">{attachment.original_filename}</div>
                          <div className="attachment-meta">
                            {Math.round(attachment.file_size / 1024)} KB • 
                            Ajouté le {formatDate(attachment.uploaded_at)}
                          </div>
                        </div>
                        <a
                          href={`${API_BASE_URL}/api/opportunities/${opportunity.id}/attachments/${attachment.id}/download`}
                          className="attachment-download"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          ⬇️ Télécharger
                        </a>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OpportunitySidebar;